import AltArrowDownIcon from '@betfan/icons/linear/Arrows/AltArrowDown.svg?react';
import { Modal } from '@material-ui/core';
import cn from 'classnames';
import pick from 'lodash/pick';
import React, { PropsWithChildren, useState } from 'react';
import './DSInputStyles.scss';
import { DSRadioGroup, DSRadioGroupOption } from './DSRadioGroup';
import { helpText } from './utils';

type DSSelectColor = 'green' | 'theme';

export type DSSelectOptions<T> = Array<DSRadioGroupOption<T>>;

interface DSSelectProps<T> extends Pick<React.HTMLAttributes<HTMLDivElement>, 'className'> {
	color?: DSSelectColor;
	label: string;
	optionModalTitle?: string;
	value?: T;
	onChange: (value: T) => void;
	options: DSSelectOptions<T>;
	error?: string;
	helpText?: string;
}

export const DSSelect = <T extends React.Key>({ color = 'theme', ...props }: PropsWithChildren<DSSelectProps<T>>) => {
	const [optionModalOpen, setOptionModalOpen] = useState<boolean>(false);

	const classes = () =>
		cn(
			'relative',
			'rounded-4',
			props.error
				? 'text-error-500 outline outline-2 outline-error-500'
				: 'hover:outline hover:outline-2 hover:outline-primary-300 dark:hover:outline-dark-500',
			{
				'bg-primary-500 text-gray-200 dark:bg-dark-300': color === 'green',
				'bg-gray-100 text-gray-700 dark:bg-dark-300 dark:text-gray-200': color === 'theme',
			},
			props.className,
		);

	const iconClasses = () =>
		cn(
			'absolute right-3 top-1/2 -translate-y-1/2',
			'w-6 h-6',
			props.error
				? 'text-error-500'
				: {
						'text-gray-100': color === 'green',
						'text-gray-700 dark:text-gray-300': color === 'theme',
				  },
		);

	const labelClasses = () =>
		cn(
			props.value ? 'top-4 text-label-xs font-normal' : 'top-1/2 text-paragraph-md font-medium',
			'transition-all',
			'pointer-events-none', // To allow for focusing the input by clicking on the label
			'select-none',
			'absolute left-3 -translate-y-1/2',
		);
	const valueClasses = () => cn('pl-3 pt-7 pb-2 pr-12 w-full h-full', color === 'green' ? 'text-gray-100' : '');
	return (
		<>
			<div>
				<div className={classes()} onClick={() => setOptionModalOpen(true)}>
					<div className={labelClasses()}>{props.label}</div>

					<button type="button" className={iconClasses()}>
						<AltArrowDownIcon />
					</button>

					<div className={valueClasses()}>
						{/* &nbsp; to maintain constant height regardless whether the value is chosen or not */}
						{props.options.find((option) => option.key === props.value)?.value || <span>&nbsp;</span>}
					</div>
				</div>
				{helpText({ color, ...pick(props, 'color', 'error', 'helpText') })}
			</div>
			<Modal
				className="flex items-center justify-center backdrop-blur-sm"
				open={optionModalOpen}
				onClose={() => setOptionModalOpen(false)}
				BackdropProps={{ className: '!bg-black !bg-opacity-75' }}
			>
				<div className="rounded-16 bg-white dark:bg-dark-200 p-3 w-full max-w-modal-sm max-h-[calc(100vh-2rem)] overflow-auto m-4">
					<div className="text-label-lg font-semibold">{props.optionModalTitle ?? props.label}</div>
					<div className="mt-4">
						<DSRadioGroup
							options={props.options}
							value={props.value}
							onChange={(value) => {
								props.onChange(value);
								setOptionModalOpen(false);
							}}
						/>
					</div>
				</div>
			</Modal>
		</>
	);
};
